import React, { useEffect, useMemo, useState } from "react";
import { useSpecialistProfile } from "../state/SpecialistProfileContext";

const AVAILABLE_AGE_GROUPS: string[] = ["Adult", "Pediatric", "Geriatric"];

type SpecialtyRow = {
  SpecialtyID: number;
  Name: string | null;
  ParentSpecialtyID?: number | null;
};

const TabClinicalProfile: React.FC = () => {
  const { formState, updateClinicalProfileField, clinicalProfileErrors } =
    useSpecialistProfile();

  const { specialtyId, ageGroups } = formState.clinicalProfile;

  const [specialties, setSpecialties] = useState<SpecialtyRow[]>([]);
  const [specialtiesLoading, setSpecialtiesLoading] = useState(false);
  const [specialtiesError, setSpecialtiesError] = useState<string>("");

  const loadSpecialties = async () => {
    setSpecialtiesLoading(true);
    setSpecialtiesError("");

    try {
      const res = await fetch("/api/specialties", {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          text || `Failed to load specialties (status ${res.status})`
        );
      }

      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format from /api/specialties");
      }

      setSpecialties(data);
    } catch (err: any) {
      setSpecialties([]);
      setSpecialtiesError(err?.message || "Failed to load specialties.");
    } finally {
      setSpecialtiesLoading(false);
    }
  };

  useEffect(() => {
    loadSpecialties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const specialtyOptions = useMemo(() => {
    return specialties
      .filter((s) => typeof s?.SpecialtyID === "number")
      .map((s) => ({
        id: String(s.SpecialtyID),
        name:
          s.Name && s.Name.trim()
            ? s.Name.trim()
            : `Specialty ${s.SpecialtyID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [specialties]);

  const handleSpecialtyChange = (value: string) => {
    updateClinicalProfileField("specialtyId", value);
  };

  const toggleAgeGroup = (value: string) => {
    const current = ageGroups || [];
    const exists = current.includes(value);

    const updated = exists
      ? current.filter((g) => g !== value)
      : [...current, value];

    updateClinicalProfileField("ageGroups", updated);
  };

  return (
    <div>
      <p style={{ marginBottom: 16 }}>
        Define the specialist&apos;s <strong>clinical focus</strong> and the{" "}
        <strong>age groups</strong> they see.
      </p>

      {/* Specialty */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Primary specialty
          <span style={{ color: "red" }}> *</span>
        </label>

        <select
          value={specialtyId}
          onChange={(e) => handleSpecialtyChange(e.target.value)}
          style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
          disabled={specialtiesLoading}
        >
          <option value="">
            {specialtiesLoading ? "Loading specialties…" : "Select specialty…"}
          </option>

          {specialtyOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        {specialtiesError && (
          <div style={{ color: "red", marginTop: 6 }}>
            Could not load specialties: {specialtiesError}
          </div>
        )}

        {!specialtiesLoading && !specialtiesError && specialtyOptions.length === 0 && (
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
            No specialties found in the database yet.
          </div>
        )}

        {clinicalProfileErrors.specialtyId && (
          <div style={{ color: "red", marginTop: 4 }}>
            {clinicalProfileErrors.specialtyId}
          </div>
        )}
      </div>

      {/* Age groups – multiselect, no "All ages" */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 4 }}>
          Age groups you serve
          <span style={{ color: "red" }}> *</span>
        </label>
        <p style={{ fontSize: 13, marginTop: 0, marginBottom: 8 }}>
          Select one or more. If multiple groups are selected, the system will
          treat this as a mixed-age practice.
        </p>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {AVAILABLE_AGE_GROUPS.map((group) => {
            const checked = (ageGroups || []).includes(group);
            return (
              <label
                key={group}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                  borderRadius: 16,
                  border: checked ? "2px solid #0017a2" : "1px solid #ccc",
                  backgroundColor: checked ? "#eef1ff" : "#fafafa",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleAgeGroup(group)}
                />
                <span>{group}</span>
              </label>
            );
          })}
        </div>

        {clinicalProfileErrors.ageGroups && (
          <div style={{ color: "red", marginTop: 4 }}>
            {clinicalProfileErrors.ageGroups}
          </div>
        )}
      </div>
    </div>
  );
};

export default TabClinicalProfile;
